/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 📄 File: components/ui/DataGridSimple.tsx
 🧩 Description:
    Advanced data grid with pagination, sorting, filtering, and
    comprehensive data management capabilities. Delegates:
    - Toolbar (search/filters/columns) → DataGridToolbar
    - Table + pagination              → DataGridTable
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import ConfirmationModal from './ConfirmationModal';
import { useSidebarState } from '../../hooks/useSidebarState';
import { DataGridToolbar } from './DataGridToolbar';
import { DataGridTable } from './DataGridTable';
import { useSidebarContext } from '../../contexts/SidebarContext';

interface Column {
    key: string; label: string; visible: boolean; sortable?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    render?: (value: any, item: any) => React.ReactNode;
    width?: string; minWidth?: string; maxWidth?: string;
    align?: 'left' | 'center' | 'right';
}

interface DataGridProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any[];
    loading: boolean;
    rowKey?: string;
    columns: Column[];
    onColumnToggle: (key: string) => void;
    pagination: { page: number; limit: number; total: number; totalPages: number; };
    onPageChange: (page: number) => void;
    onItemsPerPageChange: (limit: number) => void;
    searchQuery: string;
    onSearch: (query: string) => void;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    onSort: (column: string) => void;
    onAdd?: () => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onEdit?: (item: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onDuplicate?: (item: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onDelete?: (item: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    renderActions?: (item: any) => React.ReactNode;
    title: string;
    titleIcon?: React.ReactNode;
    description?: string;
    addButtonText?: string;
    searchPlaceholder?: string;
    emptyMessage?: string;
    filters?: React.ReactNode;
    actions?: React.ReactNode;
    deleteTitle?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    deleteMessage?: (item: any) => string;
    translations?: {
        items?: string; columns?: string; noItemsFound?: string; showing?: string; to?: string; of?: string;
        results?: string; previous?: string; next?: string; page?: string; deleteItem?: string;
        areYouSureDeleteItem?: string; delete?: string; cancel?: string; allSelected?: string;
        selected?: string; searchOptions?: string; noOptionsFound?: string; selectAll?: string; clearAll?: string;
    };
    disableFixedHeader?: boolean;
}

function DataGridSimple({
    data, loading, rowKey = 'id', columns, onColumnToggle,
    pagination, onPageChange, onItemsPerPageChange,
    searchQuery, onSearch, sortBy, sortOrder, onSort,
    onAdd, onEdit, onDuplicate, onDelete, renderActions,
    title, titleIcon, description,
    addButtonText = 'Add Item', searchPlaceholder = 'Search...', emptyMessage = 'No items found',
    deleteTitle = 'Delete Item', deleteMessage, translations, filters, actions,
    disableFixedHeader = false,
}: DataGridProps) {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [itemToDelete, setItemToDelete] = useState<any>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [localSearchInput, setLocalSearchInput] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const { width: sidebarWidth } = useSidebarState();
    const effectiveSidebarWidth = sidebarWidth === 0 ? 350 : sidebarWidth;

    let isMobileView = false;
    try {
        const sidebarCtx = useSidebarContext();
        isMobileView = sidebarCtx.isMobileView;
    } catch {
        isMobileView = window.innerWidth < 1024;
    }

    const actualDisableFixedHeader = disableFixedHeader || isMobileView;

    const visibleColumns = useMemo(() => (columns || []).filter((c) => c && c.visible !== false), [columns]);

    // Sync local search with parent
    useEffect(() => { setLocalSearchInput(searchQuery || ''); }, [searchQuery]);

    useEffect(() => {
        if (searchQuery) { setIsSearching(true); const t = setTimeout(() => setIsSearching(false), 500); return () => clearTimeout(t); }
        setIsSearching(false);
    }, [searchQuery]);

    const handleSearchInputChange = useCallback((value: string) => {
        setLocalSearchInput(value);
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        if (value.length >= 2 || value.length === 0) {
            setIsSearching(true);
            searchTimeoutRef.current = setTimeout(() => setIsSearching(false), 1000);
        }
        onSearch(value);
    }, [onSearch]);

    const handleClearSearch = useCallback(() => { setLocalSearchInput(''); onSearch(''); }, [onSearch]);

    const handleDeleteClick = useCallback((item: any) => { setItemToDelete(item); setShowDeleteModal(true); }, []);

    const confirmDelete = useCallback(async () => {
        if (!itemToDelete || !onDelete) return;
        try { setIsDeleting(true); await onDelete(itemToDelete); setShowDeleteModal(false); setItemToDelete(null); }
        catch (err) { console.error('Error deleting item:', err); }
        finally { setIsDeleting(false); }
    }, [itemToDelete, onDelete]);

    const cancelDelete = useCallback(() => { setShowDeleteModal(false); setItemToDelete(null); setIsDeleting(false); }, []);

    const getDeleteMessage = useCallback(() => {
        if (!deleteMessage || !itemToDelete) return translations?.areYouSureDeleteItem || 'Are you sure you want to delete this item?';
        try { return deleteMessage(itemToDelete); } catch { return translations?.areYouSureDeleteItem || 'Are you sure you want to delete this item?'; }
    }, [deleteMessage, itemToDelete, translations]);

    return (
        <div className="space-y-6">
            <DataGridToolbar
                title={title} titleIcon={titleIcon} columns={columns} onColumnToggle={onColumnToggle}
                pagination={pagination} onItemsPerPageChange={onItemsPerPageChange}
                localSearchInput={localSearchInput} searchPlaceholder={searchPlaceholder}
                isSearching={isSearching} onSearchInputChange={handleSearchInputChange}
                onClearSearch={handleClearSearch} filters={filters} actions={actions}
                onAdd={onAdd} addButtonText={addButtonText} translations={translations}
                disableFixedHeader={actualDisableFixedHeader} effectiveSidebarWidth={effectiveSidebarWidth}
            />

            {/* Table container */}
            <div className="bg-card border border-border rounded-lg overflow-hidden relative" style={{ minHeight: '180px' }}>
                {!data || data.length === 0 ? (
                    loading ? (
                        <div className="flex items-center justify-center h-48">
                            <div className="flex items-center gap-3">
                                <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                                <span className="text-sm text-muted-foreground">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center h-48">
                            <div className="text-center">
                                <div className="text-muted-foreground text-lg mb-2">{translations?.noItemsFound || 'No items found'}</div>
                                <div className="text-muted-foreground text-sm">{emptyMessage}</div>
                            </div>
                        </div>
                    )
                ) : (
                    <DataGridTable
                        data={data} loading={loading} rowKey={rowKey}
                        visibleColumns={visibleColumns} sortBy={sortBy} sortOrder={sortOrder}
                        onSort={onSort} pagination={pagination} onPageChange={onPageChange}
                        renderActions={renderActions} translations={translations}
                    />
                )}

                {/* Refresh loading overlay */}
                {loading && data && data.length > 0 && (
                    <div className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-[1px] rounded-lg">
                        <div className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg shadow-lg">
                            <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                            <span className="text-sm text-muted-foreground">Loading...</span>
                        </div>
                    </div>
                )}
            </div>

            {onDelete && (
                <ConfirmationModal
                    isOpen={showDeleteModal} onClose={cancelDelete} onConfirm={confirmDelete}
                    title={deleteTitle || translations?.deleteItem || 'Delete Item'}
                    message={getDeleteMessage()} confirmText={translations?.delete || 'Delete'}
                    cancelText={translations?.cancel || 'Cancel'} type="danger" isLoading={isDeleting}
                />
            )}
        </div>
    );
}

export default memo(DataGridSimple);
